import moment from 'moment';
import { FormFieldType, ICompany, IContact, type IFormField } from '../../../extViewmodels';
import { getPartialFormattedPhoneNumber } from '../../../models/utils/phoneUtils';
import { type ILeadInteraction, ILeadView, IRelatedLink } from '../../viewModels/leads/interfaces';
import { type FollowUpImportance } from '../../viewModels/queue';

/**
 * One phone number on a contact, formatted for display in the rail.
 *
 *   - `e164` is the canonical international format used for dialing. The
 *     Twilio gateway requires this; passing the looser `standard` form
 *     causes outbound dials to be hung up by the gateway with code 31005.
 *   - `standard` is what we render and copy to the clipboard.
 *   - `display` is the UI-formatted version (US gets parens/dash; international
 *     is left as-is).
 *   - `label` is whatever the CRM has tagged it with ("Default", "Mobile",
 *     "Direct", etc.).
 */
export type FocusedLeadContactPhone = {
	label?: string;
	e164: string;
	standard: string;
	display: string;
	extension?: string;
	isInternational: boolean;
};

/**
 * One email address on a contact, projected for display in the rail.
 *
 * Compliance note: `isUnsubscribed` is *contact-level* on the backend
 * (`IContact.metadata.unsubscribeMetadata.date`), but the rail surfaces it
 * per-row. When the contact is unsubscribed, every email on the contact is
 * flagged so the rep doesn't accidentally send to any of them. The legacy
 * `CompanyContacts` UI takes the same approach.
 */
export type FocusedLeadContactEmail = {
	value: string;
	label?: string;
	isPrimary?: boolean;
	isUnsubscribed?: boolean;
};

export type FocusedLeadContact = {
	id: string;
	name: string;
	firstName?: string;
	lastName?: string;
	title?: string;
	/** Convenience: the primary email value, for callers that only want one string. */
	email?: string;
	/** All emails on the contact, in display order (primary first). */
	emailAddresses: FocusedLeadContactEmail[];
	phones: FocusedLeadContactPhone[];
	/** Profile picture URL, if any. Populated from `IContact.profilePic`. */
	imageUrl?: string;
	initials: string;
	primary?: boolean;
	/**
	 * The raw API contact this projection was built from. Kept only so the
	 * edit-contact flow can PUT a *complete* `IContact` (merging edits over the
	 * original) — the backend rejects partial updates, matching the legacy
	 * `useSaveContact` body `{ ...contact, company, companyId }`. Do NOT read
	 * this for rendering; use the projected fields above.
	 */
	raw?: IContact;
};

/**
 * View-friendly projection of `ILeadView` consumed by the Focused Lead View
 * UI. The UI never touches raw API shapes or MobX viewmodels; it reads from
 * this trimmed, display-ready structure instead.
 *
 * Fields that require runtime context (humanized `lastContact`, pending
 * `outcome` copy, etc.) are left optional — downstream hooks (call pipeline,
 * outcome mutations) layer them in via react-query cache updates.
 */
/** One editable HubSpot attribute (a single- or multi-select form field). */
export type FocusedLeadAttribute = {
	/** Form field id — the `{fieldId}` for the options + update endpoints. */
	id: string;
	label: string;
	fieldType: FormFieldType;
	/** Current display value (selected option label(s)); undefined when unset. */
	value?: string;
	/** Color of the selected option, when it carries one. */
	hexColor?: string;
};

export type FocusedLead = {
	companyId: string;
	company: string;
	/**
	 * Per-serve attribution id stamped by the queue when this lead was served
	 * (`ILeadView.leadServedId`). Optional — search/share/inbound landings have
	 * no served id. Threaded into the AI practice ("Spar") session so the
	 * backend can associate the practice call with the serve event; the
	 * outbound real-dial / wrap-up flows don't read it.
	 */
	leadServedId?: string;
	website?: string;
	websiteUrl?: string;
	location?: string;
	timezone?: string;
	/** Humanized last-contact string, e.g. "Less than an hour ago". Populated by the call pipeline. */
	lastContact?: string;
	dealStage?: string;
	/**
	 * Id of the lead's most recent deal, when present. Surfaced for the
	 * wrap-up flow so `NewDemo` / `AppointmentRescheduled` outcomes can mount
	 * `DealForm` in Update mode against the existing deal — matches legacy
	 * `lead.loadDealForm(lastDeal.id)` (`QueueNoteEditor/index.tsx:336`).
	 */
	lastDealId?: string;
	/** e.g. "Active Deal". Populated downstream. */
	dealType?: string;
	/** e.g. "Pending Outcome". Populated downstream. */
	outcome?: string;
	crmTags: Array<{ label: string; kind?: string }>;
	statusFlags: Array<{ label: string; hexColor?: string; description?: string }>;
	followUp?: { date?: string; importance?: FollowUpImportance; name?: string; scheduled?: boolean };
	attributes: FocusedLeadAttribute[];
	/**
	 * Company-level phone numbers (i.e. associated with the lead, not any
	 * individual contact on it). Editable via the "Manage phone numbers"
	 * dialog from the LEAD column.
	 */
	companyPhones: FocusedLeadContactPhone[];
	/**
	 * External "related" links for the lead (HubSpot, Salesforce, etc.).
	 * Comes from `ILeadView.relatedLinks` — only populated when the
	 * account has the corresponding integration set up.
	 */
	relatedLinks: IRelatedLink[];
	/**
	 * Projection of `ICompany.socialProfiles`, indexed by lowercased provider
	 * id. Only providers with a non-empty `url` are surfaced — the LEFT column
	 * hides the icon entirely when the URL is missing, mirroring legacy
	 * `SocialProfiles` behavior.
	 */
	socialProfiles: {
		linkedIn?: string;
		twitter?: string;
		facebook?: string;
	};
	/**
	 * Raw `ICompany` snapshot kept around for in-place edit (the `Edit
	 * company details` dialog needs the full company shape so the PUT body
	 * matches the backend's expectation of a complete entity). Internal —
	 * never read by the UI for display.
	 */
	rawCompany?: ICompany;
	contacts: FocusedLeadContact[];
	/**
	 * Source of truth for who's a decision maker. The per-contact `isDecisionMaker`
	 * field on `IContact` lags behind this list, so we derive `contact.primary`
	 * from this set in `withContacts` rather than trusting the contact payload.
	 */
	decisionMakerIds: string[];
	/**
	 * Id of the server-resolved outbound caller-ID ("from") number suggested for
	 * this lead (`ILeadView.suggestedTelephonyConfiguration`). The API resolves
	 * this per lead fetch — rotating across the rep's connected outbound numbers
	 * and matching local-presence area codes. The dial path forwards it as the
	 * `preferredTelephonyConfigurationId`, mirroring the legacy queue
	 * (`CallingCard` sets `suggestedTelephonyConfiguration || userSession.telephonyConfiguration`).
	 * Undefined when the API has no suggestion (e.g. the rep has a single
	 * number), in which case the dial path falls back to
	 * `userSession.telephonyConfiguration`.
	 *
	 * Without this, every focused-view call pinned the rep's single default
	 * number and bypassed the API's outbound-number rotation entirely.
	 */
	suggestedTelephonyConfigurationId?: string;
	/**
	 * E.164 of the suggested "from" number, surfaced so the call strip's
	 * "Calling from …" sub-line shows the number the call will actually
	 * originate from rather than the rep's default.
	 */
	suggestedFromNumberE164?: string;
};

/**
 * Returns two uppercase initials derived from the first character of the
 * first and last name fragments. Falls back gracefully when a name is
 * missing a space or is empty.
 */
function computeInitials(first?: string, last?: string, fallback?: string): string {
	const f = (first ?? '').trim();
	const l = (last ?? '').trim();
	if (f || l) {
		return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
	}
	const fb = (fallback ?? '').trim();
	if (!fb) {
		return '';
	}
	const parts = fb.split(/\s+/);
	if (parts.length >= 2) {
		return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
	}
	return fb.charAt(0).toUpperCase();
}

/**
 * Normalizes a raw URL-ish string (possibly missing a protocol) into a
 * navigable `https://` URL. Returns `undefined` when the input is empty.
 */
function toWebsiteUrl(raw?: string): string | undefined {
	const trimmed = (raw ?? '').trim();
	if (!trimmed) {
		return undefined;
	}
	if (/^https?:\/\//i.test(trimmed)) {
		return trimmed;
	}
	return `https://${trimmed}`;
}

/**
 * Composes a human-readable location from an `IAddress`. Prefers
 * "City, State" when both are present; otherwise returns whichever field is
 * available, or `undefined` if neither exists.
 */
function formatLocation(address?: { city?: string; stateProvince?: string }): string | undefined {
	if (!address) {
		return undefined;
	}
	const city = (address.city ?? '').trim();
	const state = (address.stateProvince ?? '').trim();
	if (city && state) {
		return `${city}, ${state}`;
	}
	return city || state || undefined;
}

/**
 * Maps `ICompany.socialProfiles` into a provider-keyed lookup, matching
 * legacy `SocialProfiles` (which keys off lowercased `typeId`). Profiles
 * without a `url` are dropped — the LEFT column hides icons that have no
 * destination instead of rendering a dead button.
 */
function projectSocialProfiles(company?: ICompany): FocusedLead['socialProfiles'] {
	const out: FocusedLead['socialProfiles'] = {};
	for (const profile of company?.socialProfiles ?? []) {
		const url = profile.url?.trim();
		if (!url) continue;
		const id = (profile.typeId ?? '').toLowerCase();
		if (id === 'linkedin') {
			out.linkedIn = url;
		} else if (id === 'twitter' || id === 'x') {
			out.twitter = url;
		} else if (id === 'facebook') {
			out.facebook = url;
		}
	}
	return out;
}

/**
 * Maps a full `ILeadView` from the API into the `FocusedLead` shape used by
 * the Focused Lead View UI. Fields that can't be derived from `ILeadView`
 * alone (contacts, live call state, humanized timestamps) are left empty or
 * undefined — those are filled in by downstream hooks.
 */
export function fromILeadView(lead: ILeadView): FocusedLead {
	const company = lead.company;
	const tags = company?.tagsCollection ?? [];

	return {
		companyId: company?.id ?? '',
		company: company?.companyName ?? '',
		leadServedId: lead.leadServedId,
		website: company?.webSite?.trim() || undefined,
		websiteUrl: toWebsiteUrl(company?.webSite),
		location: formatLocation(company?.address),
		timezone: lead.abbreviatedTimeZone || company?.timeZone || undefined,
		dealStage: lead.lastDeal?.stage || undefined,
		lastDealId: lead.lastDeal?.id || undefined,
		crmTags: tags.map(tag => ({
			label: tag.name ?? '',
			kind: tag.category?.name,
		})),
		statusFlags: (lead.statusFlags ?? []).map(flag => ({
			label: flag.name,
			hexColor: flag.hexColor,
			description: flag.description,
		})),
		followUp: lead.followUp
			? {
					date: lead.followUp.followUpDate,
					importance: lead.followUp.importance,
					name: lead.followUp.name,
					scheduled: lead.followUp.scheduled,
				}
			: undefined,
		attributes: (lead.attributesForm?.fields ?? [])
			.filter(
				field =>
					// Require an id or the pill PUTs a malformed `…/Lead/Field/` URL and collides React keys.
					!!field.id &&
					!field.isHidden &&
					(field.fieldType === FormFieldType.Option || field.fieldType === FormFieldType.MultipleOptions)
			)
			.map(projectAttribute),
		companyPhones: (company?.phoneNumbers ?? []).map(toFocusedPhone).filter((p): p is FocusedLeadContactPhone => !!p),
		relatedLinks: lead.relatedLinks ?? [],
		socialProfiles: projectSocialProfiles(company),
		lastContact: humanizeLastContact(lead),
		rawCompany: company,
		// `ILeadView` does not carry contacts directly — `LeadViewModel` fetches
		// them via a paged collection. When contact data is layered in via
		// `withContacts`, `primary` is derived from `decisionMakerIds`.
		contacts: [],
		decisionMakerIds: lead.decisionMakersContactIds ?? [],
		// Forward the API's per-lead outbound-number suggestion so the dial path
		// can let the server resolve the "from" number (rotation + local
		// presence) instead of pinning the rep's default. Mirrors the legacy
		// queue's `CallingCard`, which seeds telephony config from the same field.
		suggestedTelephonyConfigurationId: lead.suggestedTelephonyConfiguration?.id,
		suggestedFromNumberE164: lead.suggestedTelephonyConfiguration?.phoneNumber?.e164,
	};
}

/**
 * Humanized last-contact string for the left-column "Last Contact" row and the
 * "Call Again" relabel. Sourced from `lastInteraction.date` — the same field
 * legacy's "Last Called:" card and the focused-view SearchResultRow use.
 *
 * `lastInteraction` is declared on `ILeadSearchView`, not `ILeadView`, so we
 * read it defensively here: when the single-lead endpoint includes it the row
 * populates; otherwise it stays empty (no regression). If it never populates,
 * the single-lead endpoint needs to return `lastInteraction` like the search
 * view does.
 */
/** Shape of an Option/MultipleOptions field's `value` (single option or array). */
type AttributeOptionValue = { label?: string; value?: string; hexColor?: string };

/**
 * Project one `attributesForm` field to a {@link FocusedLeadAttribute}. Reads the
 * selected option's label (joining multiselect) and its color when present; an
 * unset field yields an undefined value (rendered as "Unknown" downstream).
 */
function projectAttribute(field: IFormField<unknown>): FocusedLeadAttribute {
	const raw = field.value;
	let value: string | undefined;
	let hexColor: string | undefined;
	if (Array.isArray(raw)) {
		const labels = (raw as AttributeOptionValue[])
			.map(option => option?.label)
			.filter((label): label is string => !!label);
		value = labels.length > 0 ? labels.join(', ') : undefined;
	} else if (raw && typeof raw === 'object') {
		const option = raw as AttributeOptionValue;
		value = option.label || option.value || undefined;
		hexColor = option.hexColor || undefined;
	}
	return { id: field.id ?? '', label: field.label, fieldType: field.fieldType, value, hexColor };
}

function humanizeLastContact(lead: ILeadView): string | undefined {
	const interaction = (lead as { lastInteraction?: ILeadInteraction }).lastInteraction;
	const date = interaction?.date;
	if (!date) return undefined;
	const parsed = moment(date);
	return parsed.isValid() ? parsed.fromNow() : undefined;
}

/**
 * Map an `IPhoneNumber` to the rail's display shape. International numbers
 * are kept verbatim because `getPartialFormattedPhoneNumber` assumes US
 * formatting; everything else gets formatted with parens/dashes.
 */
function toFocusedPhone(p: NonNullable<IContact['phoneNumbers']>[number]): FocusedLeadContactPhone | undefined {
	const standard = (p.metadata?.standard ?? p.value ?? '').trim();
	if (!standard) return undefined;
	const e164 = p.metadata?.e164?.trim() || standard;
	const isInternational = !!p.metadata?.countryCode && p.metadata.countryCode !== 1 && !!p.metadata.standard;
	const baseStandard = standard.split('ext')[0].trim();
	const display = isInternational ? standard : getPartialFormattedPhoneNumber(baseStandard);
	return {
		label: p.label?.trim() || undefined,
		e164,
		standard,
		display,
		extension: p.metadata?.extension || undefined,
		isInternational,
	};
}

/**
 * Build the per-row email projection. Unsubscribe is contact-level on the
 * backend; we copy that flag onto every row so the UI can show a pill on
 * each address without re-checking the contact metadata. The contact's
 * `primaryEmail` is hoisted to the front so `email` (the convenience field)
 * always matches the first row.
 */
function projectEmails(contact: IContact): FocusedLeadContactEmail[] {
	const isUnsubscribed = !!contact.metadata?.unsubscribeMetadata?.date;
	const primaryValue = contact.primaryEmail?.value?.trim();
	const seen = new Set<string>();
	const out: FocusedLeadContactEmail[] = [];

	if (primaryValue) {
		out.push({
			value: primaryValue,
			label: contact.primaryEmail?.label,
			isPrimary: true,
			isUnsubscribed,
		});
		seen.add(primaryValue.toLowerCase());
	}
	for (const e of contact.emailAddresses ?? []) {
		const value = e?.value?.trim();
		if (!value) continue;
		const key = value.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		out.push({
			value,
			label: e.label,
			isPrimary: out.length === 0,
			isUnsubscribed,
		});
	}
	return out;
}

export function toFocusedLeadContact(contact: IContact): FocusedLeadContact {
	const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
	const emailAddresses = projectEmails(contact);
	const email = emailAddresses[0]?.value;
	const phones = (contact.phoneNumbers ?? []).map(toFocusedPhone).filter((p): p is FocusedLeadContactPhone => !!p);
	const displayName = fullName || email || phones[0]?.standard || 'Unnamed contact';
	return {
		id: contact.id ?? '',
		name: displayName,
		firstName: contact.firstName,
		lastName: contact.lastName,
		title: contact.jobTitle,
		email,
		emailAddresses,
		phones,
		imageUrl: contact.profilePic || undefined,
		initials: computeInitials(contact.firstName, contact.lastName, displayName),
		// `primary` (= decision maker) is set by `withContacts` from the lead's
		// `decisionMakerIds` since the per-contact flag isn't a reliable source
		// of truth across mutations.
		primary: undefined,
		raw: contact,
	};
}

export function withContacts(lead: FocusedLead, contacts: FocusedLeadContact[]): FocusedLead {
	const dmSet = new Set(lead.decisionMakerIds);
	const decorated = contacts.map(c => ({ ...c, primary: dmSet.has(c.id) || undefined }));
	// Match the legacy CompanyContacts ordering — decision makers first.
	const sorted = [...decorated].sort((a, b) => {
		const aDm = a.primary ? 1 : 0;
		const bDm = b.primary ? 1 : 0;
		return bDm - aDm;
	});
	return { ...lead, contacts: sorted };
}
